import React, { useState } from 'react';
import { createReview } from '../../services/reviewService';
import { uploadReviewPhoto } from '../../services/discoveryService';

const MAX_PHOTOS = 5;
const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'];

// Write-a-review form (stars, title, text, up to 5 photos).
// Used on the product page and the order-success page.
const ReviewForm = ({ productId, productName, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]); // { url } | { uploading, name }
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const uploading = photos.some((p) => p.uploading);

  const addPhotos = async (fileList) => {
    setError('');
    const files = Array.from(fileList || []).slice(0, MAX_PHOTOS - photos.length);
    for (const file of files) {
      if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
        setError('Photos must be JPG, PNG or WebP.');
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(`${file.name} is larger than 5 MB.`);
        continue;
      }
      const key = `${file.name}-${file.size}-${Date.now()}`;
      const preview = URL.createObjectURL(file);
      setPhotos((prev) => [...prev, { key, preview, uploading: true }]);
      try {
        const url = await uploadReviewPhoto(file);
        setPhotos((prev) => prev.map((p) => (p.key === key ? { key, preview, url } : p)));
      } catch (err) {
        setPhotos((prev) => prev.filter((p) => p.key !== key));
        setError(err.response?.data?.message || 'A photo could not be uploaded. Please try again.');
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return setError('Please choose a star rating.');
    if (comment.trim().length < 10) return setError('Please write at least a sentence about the product.');
    if (uploading) return setError('Please wait for your photos to finish uploading.');
    setSubmitting(true);
    setError('');
    try {
      await createReview({
        product: productId,
        rating,
        title: title.trim(),
        comment: comment.trim(),
        images: photos.filter((p) => p.url).map((p) => p.url)
      });
      setDone(true);
      onSubmitted?.(productId);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not submit your review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="m-modal-backdrop" onClick={onClose}>
      <div className="m-modal m-review-form" role="dialog" aria-modal="true" aria-label="Write a review" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="m-modal-close" onClick={onClose} aria-label="Close">×</button>

        {done ? (
          <div className="m-review-done">
            <h3>Thank you!</h3>
            <p>Your review has been sent. It will appear on the product page after a quick check by our team.</p>
            <button type="button" className="m-btn" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3>Write a review</h3>
            {productName && <p className="m-review-product">{productName}</p>}

            <fieldset className="m-stars-input">
              <legend>Your rating</legend>
              <div onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    className={(hover || rating) >= n ? 'is-on' : ''}
                    onMouseEnter={() => setHover(n)}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    aria-pressed={rating === n}
                  >
                    ★
                  </button>
                ))}
                <span className="m-stars-label">{LABELS[hover || rating]}</span>
              </div>
            </fieldset>

            <label className="m-field">
              <span>Title (optional)</span>
              <input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Lovely fabric, true to size" />
            </label>

            <label className="m-field">
              <span>Your review</span>
              <textarea
                rows={4}
                value={comment}
                maxLength={1500}
                onChange={(e) => setComment(e.target.value)}
                placeholder="How was the fabric, fit and colour? Would you recommend it?"
              />
            </label>

            <div className="m-field">
              <span>Photos (optional, up to {MAX_PHOTOS})</span>
              <div className="m-photo-row">
                {photos.map((p) => (
                  <div className={`m-photo ${p.uploading ? 'is-uploading' : ''}`} key={p.key}>
                    <img src={p.preview} alt="" />
                    {!p.uploading && (
                      <button
                        type="button"
                        aria-label="Remove photo"
                        onClick={() => setPhotos((prev) => prev.filter((x) => x.key !== p.key))}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <label className="m-photo-add">
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} />
                    <span aria-hidden="true">+</span>
                    <small>Add photo</small>
                  </label>
                )}
              </div>
            </div>

            {error && <p className="m-form-error" role="alert">{error}</p>}

            <div className="m-form-actions">
              <button type="button" className="m-btn light" onClick={onClose}>Cancel</button>
              <button type="submit" className="m-btn" disabled={submitting || uploading}>
                {submitting ? 'Submitting…' : 'Submit review'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReviewForm;
